import {ColDef} from 'ag-grid-enterprise';

/**
 * Column-visibility persistence for grids with a column chooser. Generalization of
 * `oms/shared/utils/column-state.ts` — the canonical persisted form is a flat JSON list of
 * field names under a caller-supplied localStorage key, so every historical key
 * ('crm:investors:columns', and every `*-table:columns` the sweep has since migrated) keeps
 * working verbatim. Parsing tolerates legacy array-of-objects payloads and stray
 * null/garbage entries (see the fixed rollers bug in e6021f71).
 */
export interface ColumnLike {
    field: string;
}

/** Resolve a persisted payload into available column objects, in saved order, dropping unknown/missing fields. */
export function resolveSavedColumns<T extends ColumnLike>(raw: string | null, available: T[]): T[] {
    return pickColumnsByField(available, parseSavedColumnFields(raw));
}

/** Pick columns by field name, in the given order, skipping any field not present in `available`. */
export function pickColumnsByField<T extends ColumnLike>(available: T[], fields: string[]): T[] {
    const byField = new Map(available.map(c => [c.field, c]));
    return fields.map(f => byField.get(f)).filter((c): c is T => !!c);
}

/** Serialize selected columns to the canonical persisted form (a flat list of field names). */
export function serializeColumnFields<T extends ColumnLike>(columns: T[]): string {
    return JSON.stringify(columns.filter(c => c).map(c => c.field));
}

/**
 * Load the visible column selection for `key`. Falls back to `defaultFields` (or all of
 * `available`) when nothing usable is persisted — mirroring the p-table lists' behavior of
 * treating a missing/empty payload as 'use the defaults'.
 */
export function loadVisibleColumns<T extends ColumnLike>(key: string, available: T[], defaultFields?: string[]): T[] {
    const raw = localStorage.getItem(key);
    if (raw && raw !== '[]') {
        const resolved = resolveSavedColumns(raw, available);
        if (resolved.length) return resolved;
    }
    return defaultFields ? pickColumnsByField(available, defaultFields) : [...available];
}

/** Persist the visible column selection for `key` in the canonical form. */
export function saveVisibleColumns(key: string, columns: ColumnLike[]): void {
    localStorage.setItem(key, serializeColumnFields(columns));
}

/**
 * Map a visible-fields selection onto colDefs as `hide` flags. Only columns whose field is in
 * `choosableFields` are touched — structural columns (selection, actions) and columns outside
 * the chooser keep their own visibility.
 */
export function applyColumnVisibility<T>(defs: ColDef<T>[], visibleFields: string[], choosableFields: string[]): ColDef<T>[] {
    const choosable = new Set(choosableFields);
    const visible = new Set(visibleFields);
    return defs.map(def =>
        def.field && choosable.has(def.field)
            ? {...def, hide: !visible.has(def.field)}
            : def,
    );
}

/** Parse a persisted payload into field names, tolerating legacy object payloads and stray null/garbage entries. */
export function parseSavedColumnFields(raw: string | null): string[] {
    if (!raw || raw === '[]') return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
        .map(entry => {
            if (typeof entry === 'string') return entry;
            if (entry && typeof entry === 'object' && 'field' in entry) {
                const field = (entry as {field: unknown}).field;
                return typeof field === 'string' ? field : null;
            }
            return null;
        })
        .filter((f): f is string => !!f);
}
